import { emptyValue } from './constants';
import { getPlotagemLabel } from './formatters';

export const normalizeText = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

export const normalizeDigits = value => String(value || '').replace(/\D/g, '');

export const getFilterValue = value => {
  if (value instanceof Date) {
    return value;
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value.value ?? '';
  }

  return value ?? '';
};

export const getFilterValues = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => getFilterValue(item))
      .filter(item => item !== '');
  }

  const singleValue = getFilterValue(value);
  return singleValue === '' ? [] : [singleValue];
};

export const hasAnyFilledFilter = filtersObject => Object.values(filtersObject || {})
  .some(value => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value;
    return getFilterValue(value) !== '';
  });

export const tokenizeFilter = value => normalizeText(value)
  .split(/\s+/)
  .filter(Boolean);

export const includesFilter = (value, filterValue) => {
  if (!filterValue) return true;
  return normalizeText(value).includes(normalizeText(filterValue));
};

export const exactMatchFilter = (value, filterValue) => {
  if (!filterValue) return true;
  return normalizeText(value) === normalizeText(filterValue);
};

export const exactMatchAnyFilter = (value, filterValue) => {
  const filterValues = getFilterValues(filterValue);

  if (!filterValues.length) return true;

  return filterValues.some(currentValue => exactMatchFilter(value, currentValue));
};

export const startsWithWordFilter = (value, filterValue) => {
  const filterTokens = tokenizeFilter(filterValue);
  if (!filterTokens.length) return true;

  const valueTokens = tokenizeFilter(value);

  return filterTokens.every(filterToken => (
    valueTokens.some(valueToken => valueToken.startsWith(filterToken))
  ));
};

export const includesDigitsFilter = (value, filterValue) => {
  const normalizedFilter = normalizeDigits(filterValue);
  if (!normalizedFilter) return true;

  return normalizeDigits(value).includes(normalizedFilter);
};

export const normalizeDateFilterValue = value => {
  if (!value) return '';

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue) return '';

  const isoMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const brMatch = normalizedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (brMatch) return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;

  const parsedDate = new Date(normalizedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().slice(0, 10);
  }

  return '';
};

export const normalizeDateTimeFilterValue = value => {
  if (!value) return '';

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue) return '';

  const isoDateTimeMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
  if (isoDateTimeMatch) {
    return `${isoDateTimeMatch[1]}-${isoDateTimeMatch[2]}-${isoDateTimeMatch[3]} ${isoDateTimeMatch[4]}:${isoDateTimeMatch[5]}`;
  }

  const brDateTimeMatch = normalizedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
  if (brDateTimeMatch) {
    return `${brDateTimeMatch[3]}-${brDateTimeMatch[2]}-${brDateTimeMatch[1]} ${brDateTimeMatch[4]}:${brDateTimeMatch[5]}`;
  }

  return '';
};

export const dateEqualsFilter = (value, filterValue) => {
  const normalizedFilter = normalizeDateFilterValue(filterValue);
  if (!normalizedFilter) return true;

  return normalizeDateFilterValue(value) === normalizedFilter;
};

export const dateTimeEqualsFilter = (value, filterValue) => {
  const normalizedFilterDateTime = normalizeDateTimeFilterValue(filterValue);
  if (normalizedFilterDateTime) {
    return normalizeDateTimeFilterValue(value) === normalizedFilterDateTime;
  }

  return dateEqualsFilter(value, filterValue);
};

export const someDateTimeEqualsFilter = (values, filterValue) => {
  if (!filterValue) return true;

  if (Array.isArray(values)) {
    return values.some(value => dateTimeEqualsFilter(value, filterValue));
  }

  return dateTimeEqualsFilter(values, filterValue);
};

export const buildGestorOptions = items => Array.from(
  new Set(
    (items || [])
      .map(item => String(item?.gestor || '').trim())
      .filter(value => value && value !== emptyValue)
  )
)
  .sort((a, b) => a.localeCompare(b))
  .map(value => ({
    value,
    label: value
  }));

export const buildCidadeOptionValue = (cidade, estado) => {
  const cidadeNormalizada = String(cidade || '').trim();
  const estadoNormalizado = String(estado || '').trim();

  return `${cidadeNormalizada}::${estadoNormalizado}`;
};

export const buildCidadeOptions = items => Array.from(
  new Map(
    (items || [])
      .map(item => {
        const cidade = String(item?.cidade || '').trim();
        const estado = String(item?.estado || '').trim();

        if (!cidade || cidade === emptyValue) return null;

        const value = buildCidadeOptionValue(cidade, estado);
        const label = estado ? `${cidade} - ${estado}` : cidade;

        return [value, { value, label }];
      })
      .filter(Boolean)
  ).values()
)
  .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

export const sortTipoOptionsByDisponibilidade = (items, getTipoValue) => {
  const statsByTipo = (items || []).reduce((acc, item) => {
    const tipoValue = String(getTipoValue(item) || '').trim();

    if (!tipoValue || tipoValue === emptyValue) return acc;

    const current = acc.get(tipoValue) || { disponiveis: 0 };
    const disponivel = item?.disponivel === true && Number(item?.statusPatio) === 1;

    if (disponivel) {
      current.disponiveis += 1;
    }

    acc.set(tipoValue, current);
    return acc;
  }, new Map());

  return [...statsByTipo.entries()]
    .sort((a, b) => {
      const disponiveisA = a[1]?.disponiveis || 0;
      const disponiveisB = b[1]?.disponiveis || 0;

      if (disponiveisA !== disponiveisB) return disponiveisB - disponiveisA;

      return a[0].localeCompare(b[0], 'pt-BR');
    })
    .map(([value]) => ({
      value,
      label: value
    }));
};

export const buildTipoTracaoOptions = items => sortTipoOptionsByDisponibilidade(items, item => item?.tipoTracao);
export const buildTipoReboqueOptions = items => sortTipoOptionsByDisponibilidade(items, item => item?.tipoReboque);
export const buildSegmentoVeiculoOptions = items => sortTipoOptionsByDisponibilidade(items, item => item?.segmentoVeiculo);
export const buildTipoOperacaoOptions = items => sortTipoOptionsByDisponibilidade(items, item => item?.tipoOperacao);
export const buildPlotagemOptions = items => sortTipoOptionsByDisponibilidade(
  items,
  item => getPlotagemLabel(item?.idTipoEmblema ?? item?.plotagem)
);

export const applyMotoristaFilters = (data, filtros) => data.filter(item => (
  startsWithWordFilter(item.motorista, getFilterValue(filtros?.NomeMotorista))
  && (!getFilterValue(filtros?.Cidade) || buildCidadeOptionValue(item.cidade, item.estado) === getFilterValue(filtros?.Cidade))
  && exactMatchFilter(item.gestor, getFilterValue(filtros?.Gestor))
  && includesDigitsFilter(item.cpf, getFilterValue(filtros?.Cpf))
  && (!getFilterValue(filtros?.Disponivel) || String(item.disponivel) === getFilterValue(filtros?.Disponivel))
));

export const applyFrotaFilters = (data, filtros) => {
  const frotaFiltro = getFilterValue(filtros?.Frota);
  const veiculoFiltro = getFilterValue(filtros?.Veiculo);
  const gestorFiltro = filtros?.Gestor;
  const cargaFiltro = getFilterValue(filtros?.Carga);
  const disponivelFiltro = getFilterValue(filtros?.Disponivel);
  const mensagemDisponibilidadeFiltro = getFilterValue(filtros?.MensagemDisponibilidade);
  const statusPatioFiltro = filtros?.StatusPatio;
  const tipoTracaoFiltro = filtros?.TipoTracao;
  const tipoReboqueFiltro = filtros?.TipoReboque;
  const dataAgendamentoFiltro = getFilterValue(filtros?.DataAgendamento);
  const dataInicioCarregamentoFiltro = getFilterValue(filtros?.DataInicioCarregamento);
  const segmentoVeiculoFiltro = filtros?.SegmentoVeiculo;
  const tipoOperacaoFiltro = filtros?.TipoOperacao;
  const plotagemFiltro = filtros?.Plotagem;
  const maisDeUmaCargaFiltro = filtros?.MaisDeUmaCarga === true;

  return data.filter(item => (
    exactMatchAnyFilter(item.frota, frotaFiltro)
    && includesFilter(item.veiculo, veiculoFiltro)
    && exactMatchAnyFilter(item.gestor, gestorFiltro)
    && someDateTimeEqualsFilter(
      item.dataAgendamentoOriginalValues || item.dataAgendamentoOriginal || item.dataAgendamento,
      dataAgendamentoFiltro
    )
    && someDateTimeEqualsFilter(
      item.dataInicioCarregamentoOriginalValues || item.dataInicioCarregamentoOriginal || item.dataInicioCarregamento,
      dataInicioCarregamentoFiltro
    )
    && includesFilter(item.cargasResumo, cargaFiltro)
    && (!disponivelFiltro || String(item.disponivel) === disponivelFiltro)
    && includesFilter(item.mensagemDisponibilidade, mensagemDisponibilidadeFiltro)
    && exactMatchAnyFilter(String(item.statusPatio), statusPatioFiltro)
    && (
      getFilterValues(tipoTracaoFiltro).length === 0
      || exactMatchAnyFilter(item.tipoTracao, tipoTracaoFiltro)
      || exactMatchAnyFilter(item.categoria, tipoTracaoFiltro)
    )
    && (
      getFilterValues(tipoReboqueFiltro).length === 0
      || exactMatchAnyFilter(item.tipoReboque, tipoReboqueFiltro)
      || exactMatchAnyFilter(item.categoria, tipoReboqueFiltro)
    )
    && exactMatchAnyFilter(item.segmentoVeiculo, segmentoVeiculoFiltro)
    && exactMatchAnyFilter(item.tipoOperacao, tipoOperacaoFiltro)
    && exactMatchAnyFilter(item.plotagem, plotagemFiltro)
    && (!maisDeUmaCargaFiltro || (Array.isArray(item.cargas) && item.cargas.length > 1))
  ));
};
